import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Cookies from 'js-cookie';
import { getBusinessQrCodeUrl, downloadBusinessQRCode } from '../services/qrCodeService';

const QRCodeManager = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const businessId = Cookies.get('businessId');
    const qrPngBlobRef = useRef(null);
    const previewObjectUrlRef = useRef(null);

    useEffect(() => {
        const revokePreviewObjectUrl = () => {
            if (previewObjectUrlRef.current) {
                URL.revokeObjectURL(previewObjectUrlRef.current);
                previewObjectUrlRef.current = null;
            }
        };

        if (!businessId) {
            setSnackbar({
                open: true,
                message: 'Business ID not found. Please refresh the page.',
                severity: 'error',
            });
            return undefined;
        }

        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setQrCodeUrl('');
            setQrPreviewUrl(null);
            qrPngBlobRef.current = null;
            revokePreviewObjectUrl();

            try {
                const [urlResult, pngResult] = await Promise.allSettled([
                    getBusinessQrCodeUrl(businessId),
                    downloadBusinessQRCode(businessId),
                ]);

                if (cancelled) {
                    return;
                }

                const failedParts = [];

                if (urlResult.status === 'fulfilled') {
                    const trimmed = String(urlResult.value ?? '').trim();
                    if (trimmed) {
                        setQrCodeUrl(trimmed);
                    } else {
                        failedParts.push('share URL');
                    }
                } else {
                    failedParts.push('share URL');
                }

                if (pngResult.status === 'fulfilled' && pngResult.value) {
                    const blob =
                        pngResult.value instanceof Blob
                            ? pngResult.value
                            : new Blob([pngResult.value], { type: 'image/png' });
                    qrPngBlobRef.current = blob;
                    const objectUrl = URL.createObjectURL(blob);
                    previewObjectUrlRef.current = objectUrl;
                    setQrPreviewUrl(objectUrl);
                } else {
                    failedParts.push('QR code image');
                }

                if (failedParts.length > 0) {
                    setSnackbar({
                        open: true,
                        message: `Failed to load ${failedParts.join(' and ')}`,
                        severity: 'error',
                    });
                }
            } finally {
                setIsLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
            revokePreviewObjectUrl();
            setQrPreviewUrl(null);
            qrPngBlobRef.current = null;
        };
    }, [businessId]);

    const handleDownloadQR = () => {
        const blob = qrPngBlobRef.current;
        if (!blob) {
            setSnackbar({
                open: true,
                message: 'QR code is not ready to download',
                severity: 'error',
            });
            return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'business-qr-code.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        setSnackbar({
            open: true,
            message: 'QR code downloaded successfully',
            severity: 'success',
        });
    };

    const handleCopyUrl = () => {
        navigator.clipboard
            .writeText(qrCodeUrl)
            .then(() => {
                setSnackbar({
                    open: true,
                    message: 'URL copied to clipboard',
                    severity: 'success',
                });
            })
            .catch(() => {
                setSnackbar({
                    open: true,
                    message: 'Failed to copy URL',
                    severity: 'error',
                });
            });
    };

    const previewSize = isMobile ? 150 : 200;

    return (
        <Grid2 container spacing={{ xs: 2, sm: 3 }}>
            {/* Header Section */}
            <Grid2 size={{ xs: 12 }}>
                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: '#0a0b0d',
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            mb: 1,
                        }}
                    >
                        Business QR Code
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#5b616e',
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        }}
                    >
                        Generate a QR code that external users can scan to access your
                        business-specific phone login page
                    </Typography>
                </Box>
            </Grid2>

            {/* QR Code Display Section */}
            <Grid2 size={{ xs: 12, md: 6 }}>
                <Paper
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: { xs: '8px', sm: '10px' },
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                        border: '1px solid #f0f0f0',
                        textAlign: 'center',
                    }}
                >
                    <Box sx={{ mb: 2 }}>
                        <QrCodeIcon
                            sx={{
                                fontSize: { xs: 24, sm: 28 },
                                color: '#1652f0',
                                mb: 1,
                            }}
                        />
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 500,
                                color: '#0a0b0d',
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                            }}
                        >
                            Scan to Login
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 3,
                            p: 2,
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                        }}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    width: previewSize,
                                    height: previewSize,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CircularProgress size={40} />
                            </Box>
                        ) : qrPreviewUrl ? (
                            <Box
                                component="img"
                                src={qrPreviewUrl}
                                alt="Business phone login QR code"
                                sx={{
                                    width: previewSize,
                                    height: previewSize,
                                    objectFit: 'contain',
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: previewSize,
                                    height: previewSize,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            />
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadQR}
                        disabled={!qrPreviewUrl}
                        sx={{
                            py: { xs: 1, sm: 1.25 },
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            backgroundColor: '#1652f0',
                            '&:hover': {
                                backgroundColor: '#1441cc',
                            },
                        }}
                    >
                        Download QR Code
                    </Button>
                </Paper>
            </Grid2>

            {/* URL Section */}
            <Grid2 size={{ xs: 12, md: 6 }}>
                <Paper
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: { xs: '8px', sm: '10px' },
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                        border: '1px solid #f0f0f0',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 500,
                            color: '#0a0b0d',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            mb: 2,
                        }}
                    >
                        Share URL
                    </Typography>

                    <TextField
                        fullWidth
                        value={qrCodeUrl}
                        variant="outlined"
                        size="small"
                        placeholder={isLoading ? 'Loading…' : ''}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleCopyUrl}
                                        edge="end"
                                        size="small"
                                        disabled={!qrCodeUrl}
                                        sx={{
                                            color: '#5b616e',
                                            '&:hover': {
                                                color: '#1652f0',
                                            },
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                backgroundColor: '#fafafa',
                            },
                        }}
                    />

                    <Typography
                        variant="body2"
                        sx={{
                            color: '#5b616e',
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            mt: 1.5,
                        }}
                    >
                        External users can scan the QR code or visit this URL to access your
                        business-specific phone login page
                    </Typography>
                </Paper>
            </Grid2>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Grid2>
    );
};

export default QRCodeManager;
